import { Button } from "@/components/ui/button";
import { CreateOrderButton } from "@/features/orders/create-order-button";
import { CreateTripButton } from "@/features/trips/create-trip-button";
import {
  WarehouseIcon,
  LayoutGridIcon,
  PlusIcon,
  SquareMousePointerIcon,
  SearchIcon,
  FilterIcon,
} from "lucide-react";
import { NavLink } from "react-router";
import { useEffect, useState, useMemo } from "react";
import { OrderSearchDialog } from "@/features/orders/order-search-dialog";
import { TripFilterDialog } from "@/features/trips/trip-filter-dialog";
import { useShortcutsStore } from "./shortcuts.store";
import { useTrips } from "@/api/trips";

// Export TripFilter type from store instead
export type { TripFilter } from "./shortcuts.store";

interface ShortcutsProps {
  unassignedPickupTasksCount: number;
  unassignedDeliveryTasksCount: number;
}

export function Shortcuts({
  unassignedPickupTasksCount,
  unassignedDeliveryTasksCount,
}: ShortcutsProps) {
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [isTripFilterDialogOpen, setIsTripFilterDialogOpen] = useState(false);

  // Get state and actions from the store
  const {
    showPickups,
    showDeliveries,
    showDepots,
    showOperators,
    tripFilter,
    isSelectingMode,
    setShowPickups,
    setShowDeliveries,
    setShowDepots,
    setShowOperators,
    setTripFilter,
    toggleSelectingMode,
    toggleAllTrips,
  } = useShortcutsStore();

  // Fetch trips for counting
  const { data: tripsResponse } = useTrips({
    status: "PLANNED,ACTIVE,COMPLETED",
  });

  const trips = useMemo(() => tripsResponse?.data ?? [], [tripsResponse]);

  // Calculate filtered trips count
  const filteredTrips = useMemo(() => {
    if (tripFilter.showAll) {
      // If showing all, potentially hide specific trips
      return tripFilter.hiddenTripIds.length > 0
        ? trips.filter((trip) => !tripFilter.hiddenTripIds.includes(trip.id))
        : trips;
    } else if (tripFilter.visibleTripIds.length > 0) {
      // Only show specific trips
      return trips.filter((trip) =>
        tripFilter.visibleTripIds.includes(trip.id)
      );
    }
    // If not showing all and no specific trips to show
    return [];
  }, [trips, tripFilter]);

  // Count of filtered trips
  const filteredTripsCount = filteredTrips.length;

  // Check if trips are shown based on tripFilter state
  const areTripsShown = tripFilter.showAll;

  useEffect(() => {
    function handleKeyPress(event: KeyboardEvent) {
      // Ignore if user is typing in an input or textarea
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA")
      ) {
        return;
      }

      let newOrderButton: HTMLElement | null;
      let newTripButton: HTMLElement | null;

      // Handle keyboard shortcuts
      switch (event.key) {
        case "P":
          setShowPickups(!showPickups);
          break;
        case "D":
          setShowDeliveries(!showDeliveries);
          break;
        case "W":
          setShowDepots(!showDepots);
          break;
        case "O":
          setShowOperators(!showOperators);
          break;
        case "T":
          toggleAllTrips();
          break;
        case "F":
          // Open search dialog with F key
          setIsSearchDialogOpen(true);
          break;
        case "Q":
          // Changed from Shift+F to Q for filter dialog
          setIsTripFilterDialogOpen(true);
          break;
        case "S":
          toggleSelectingMode();
          break;
        case "V":
          window.location.href = "/orders";
          break;
        case "N":
          newOrderButton = document.querySelector(
            '[title="Create New Order (N)"]'
          );
          if (newOrderButton instanceof HTMLElement) {
            newOrderButton.click();
          }
          break;
        case "R":
          newTripButton = document.querySelector(
            '[title="Create New Trip (R)"]'
          );
          if (newTripButton instanceof HTMLElement) {
            newTripButton.click();
          }
          break;
      }
    }

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [
    showPickups,
    showDeliveries,
    showDepots,
    showOperators,
    tripFilter,
    isSelectingMode,
    setShowPickups,
    setShowDeliveries,
    setShowDepots,
    setShowOperators,
    setTripFilter,
    toggleSelectingMode,
    toggleAllTrips,
  ]);

  return (
    <>
      <div className="absolute top-4 right-4 z-10 grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          className={`w-full h-full aspect-square p-0 font-semibold relative ${
            showPickups ? "bg-blue-600 text-white hover:bg-blue-700" : ""
          }`}
          onClick={() => setShowPickups(!showPickups)}
          title="Toggle Pickups (P)"
        >
          P
          {unassignedPickupTasksCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
              {unassignedPickupTasksCount}
            </span>
          )}
        </Button>
        <Button
          variant="outline"
          className={`w-full h-full aspect-square p-0 font-semibold relative ${
            showDeliveries ? "bg-purple-400 text-white hover:bg-purple-500" : ""
          }`}
          onClick={() => setShowDeliveries(!showDeliveries)}
          title="Toggle Deliveries (D)"
        >
          D
          {unassignedDeliveryTasksCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
              {unassignedDeliveryTasksCount}
            </span>
          )}
        </Button>
        <Button
          variant="outline"
          className={`w-full h-full aspect-square p-0 ${
            showDepots ? "bg-blue-500 text-white hover:bg-blue-600" : ""
          }`}
          onClick={() => setShowDepots(!showDepots)}
          title="Toggle Depots (W)"
        >
          <WarehouseIcon size={24} />
        </Button>
        <Button
          variant="outline"
          className={`w-full h-full aspect-square p-0 font-semibold ${
            showOperators ? "bg-green-600 text-white hover:bg-green-700" : ""
          }`}
          onClick={() => setShowOperators(!showOperators)}
          title="Toggle Operators (O)"
        >
          O
        </Button>
        <Button
          variant="outline"
          className={`w-full h-full aspect-square p-0 font-semibold ${
            areTripsShown ? "bg-yellow-500 text-white hover:bg-yellow-600" : ""
          }`}
          onClick={toggleAllTrips}
          title="Toggle All Trips (T)"
        >
          T
        </Button>
        <Button
          variant="outline"
          className="w-full h-full aspect-square p-0 relative"
          onClick={() => setIsTripFilterDialogOpen(true)}
          title="Filter Trips (Q)"
        >
          <FilterIcon size={24} />
          {filteredTripsCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
              {filteredTripsCount}
            </span>
          )}
        </Button>
        <Button
          variant="outline"
          className={`w-full h-full aspect-square p-0 ${
            isSelectingMode ? "bg-red-500 text-white hover:bg-red-600" : ""
          }`}
          onClick={toggleSelectingMode}
          title={isSelectingMode ? "Cancel Selection (S)" : "Select Tasks (S)"}
        >
          <SquareMousePointerIcon size={24} />
        </Button>
        <Button
          variant="outline"
          className="w-full h-full aspect-square flex items-center justify-center bg-amber-500 text-white hover:bg-amber-600"
          onClick={() => setIsSearchDialogOpen(true)}
          title="Search Orders (F)"
        >
          <SearchIcon size={24} />
        </Button>
        <NavLink to="/orders">
          <Button
            variant="outline"
            className="w-full h-full aspect-square flex items-center justify-center"
            title="View Orders (V)"
          >
            <LayoutGridIcon size={24} />
          </Button>
        </NavLink>
        <CreateOrderButton
          trigger={
            <Button
              variant="outline"
              className="w-full h-16 col-span-2 flex items-center justify-start gap-2 bg-white hover:bg-gray-50"
              title="Create New Order (N)"
            >
              <PlusIcon size={24} />
              <span className="font-medium">New Order</span>
            </Button>
          }
        />
        <CreateTripButton
          trigger={
            <Button
              variant="outline"
              className="w-full h-16 col-span-2 flex items-center justify-start gap-2 bg-white hover:bg-gray-50"
              title="Create New Trip (R)"
            >
              <PlusIcon size={24} />
              <span className="font-medium">New Trip</span>
            </Button>
          }
        />
      </div>

      {/* Order Search Dialog */}
      <OrderSearchDialog
        isOpen={isSearchDialogOpen}
        onClose={() => setIsSearchDialogOpen(false)}
      />

      {/* Trip Filter Dialog */}
      <TripFilterDialog
        isOpen={isTripFilterDialogOpen}
        onClose={() => setIsTripFilterDialogOpen(false)}
        tripFilter={tripFilter}
        onFilterChange={setTripFilter}
      />
    </>
  );
}
